import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Building2, User, FileText, Loader2, FileSignature, Banknote, CheckCircle2, RefreshCw, Sparkles, HardHat, Home } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { PAYS_OPTIONS } from './SouscriptionWorkflow/constants';
import { StepIndicator } from './SouscriptionWorkflow/shared';
import StepMemberSelection from './SouscriptionWorkflow/StepMemberSelection';
import StepEntrepriseForm from './SouscriptionWorkflow/StepEntrepriseForm';
import StepMaitreOuvrage from './SouscriptionWorkflow/StepMaitreOuvrage';
import StepRecapDocuments from './SouscriptionWorkflow/StepRecapDocuments';
import StepSignatureSMS from './SouscriptionWorkflow/StepSignatureSMS';
import StepPaiement from './SouscriptionWorkflow/StepPaiement';
import StepConfirmation from './SouscriptionWorkflow/StepConfirmation';
import { getToken } from '../services/tokenService';
import axios from 'axios';

// Custom hooks
import useSiretValidation from './SouscriptionWorkflow/hooks/useSiretValidation';
import useDocuments from './SouscriptionWorkflow/hooks/useDocuments';
import useSignature from './SouscriptionWorkflow/hooks/useSignature';
import useWorkflowPersistence from './SouscriptionWorkflow/hooks/useWorkflowPersistence';
import useSubscriptionCreation from './SouscriptionWorkflow/hooks/useSubscriptionCreation';
import useMemberSelection from './SouscriptionWorkflow/hooks/useMemberSelection';

import { getDashboardRouteForRole } from '../utils/roleNavigation';

const API = '/api';

export default function SouscriptionAnnuelleWorkflow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const calculData = location.state;
  const resumeId = searchParams.get('resume');
  const propositionDevisId = searchParams.get('proposition');
  // Le snapshot est chargé pour tout `?proposition=...` (que `from` soit
  // `batico` après simulateur, `membres` après clic Souscrire dans le tableau
  // des membres, ou absent). Cela garantit que les coordonnées entreprise,
  // l'opération nominative, le maître d'ouvrage et les montants soient
  // pré-remplis depuis la source de vérité backend `batico_propositions`
  // sans aucune ressaisie jusqu'à l'étape « Documents avant signature ».
  const _fromBatico = searchParams.get('from') === 'batico';
  const shouldLoadProposition = !!propositionDevisId;
  const prefillAppliedRef = useRef(false);

  // Source de vérité du devis ESS : si l'utilisateur arrive depuis le
  // simulateur BATICO via `?proposition=...&from=batico`, on charge le
  // snapshot stocké côté backend pour récupérer le « Montant Global »
  // officiel. Évite que le workflow recalcule un montant divergent quand
  // location.state est partiel ou que l'utilisateur revient depuis un
  // lien Brevo après plusieurs jours (sessionStorage purgé).
  const [propositionSnapshot, setPropositionSnapshot] = useState(null);

  useEffect(() => {
    if (!shouldLoadProposition || !user) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = getToken();
        const res = await axios.get(
          `${API}/batico/proposition/by-devis/${encodeURIComponent(propositionDevisId)}/snapshot`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: ctrl.signal,
          },
        );
        if (res.data) setPropositionSnapshot(res.data);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          // Silencieux : on retombera sur le calculData local si l'API échoue.
          // eslint-disable-next-line no-console
          console.warn('Snapshot proposition non chargé:', err.message);
          if (err.response?.status === 404) {
            toast.error('Proposition introuvable ou sans montant', {
              description: 'Relancez le simulateur BATICO pour générer une proposition chiffrée.',
              duration: 7000,
            });
          }
        }
      }
    })();
    return () => ctrl.abort();
  }, [propositionDevisId, shouldLoadProposition, user]);

  // Core state
  const [etape, setEtape] = useState(1);
  const [membresAdherents, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('nouveau');
  const [souscriptionCreee, setSouscriptionCreee] = useState(null);
  // V36.52 — Mode de paiement RCDS lift en parent pour pouvoir conditionner
  // l'affichage du bloc SEPA dans StepConfirmation (utilisateur 01/06/2026 :
  // "lorsque la totalité est réglée par virement, ne pas afficher la page SEPA").
  const [paiementEchelonne, setPaiementEchelonne] = useState(false);
  const [selectedPays, setSelectedPays] = useState(PAYS_OPTIONS[0]);
  const [selectedPaysTelephone, setSelectedPaysTelephone] = useState(PAYS_OPTIONS[0]);

  const [formData, setFormData] = useState({
    membre_id: '', raison_sociale: '', siret: '', code_naf: '',
    forme_juridique: '', nom_gerant: '', prenom_gerant: '',
    fonction_gerant: 'Gerant', adresse_entreprise: '', code_postal: '',
    ville: '', pays: 'FR', territoire: 'metropole',
    // V36 — Multi-territoires (initialement vide, pré-rempli depuis simulateur/membre)
    territoires: [], territoires_ca: {},
    indicatif_telephone: '+33', telephone: '', email: '', site_web: '',
    chiffre_affaires: calculData?.ca_ht || 0, effectif: '',
    date_creation_entreprise: '',
    maitre_ouvrage_type: '', maitre_ouvrage_raison_sociale: '',
    maitre_ouvrage_siret: '', maitre_ouvrage_nom: '', maitre_ouvrage_prenom: '',
    maitre_ouvrage_adresse: '', maitre_ouvrage_code_postal: '',
    maitre_ouvrage_ville: '', maitre_ouvrage_pays: 'France',
    maitre_ouvrage_telephone: '', maitre_ouvrage_email: '', maitre_ouvrage_pieces_identite: []
  });

  // Maître d'ouvrage saisi dans le simulateur → pré-remplit l'étape (modifiable)
  useEffect(() => {
    const mo = propositionSnapshot?.maitre_ouvrage || propositionSnapshot?.payload?.maitre_ouvrage || calculData?.maitre_ouvrage;
    if (!mo?.type && !mo?.raison_sociale && !mo?.nom) return;
    setFormData((prev) => (prev.maitre_ouvrage_type ? prev : {
      ...prev,
      maitre_ouvrage_type: ['particulier', 'personne_physique'].includes(mo.type) ? 'particulier' : 'professionnel',
      maitre_ouvrage_raison_sociale: mo.raison_sociale || '', maitre_ouvrage_siret: mo.siret || '',
      maitre_ouvrage_nom: mo.nom || '', maitre_ouvrage_prenom: mo.prenom || '',
      maitre_ouvrage_adresse: mo.adresse || '', maitre_ouvrage_code_postal: mo.code_postal || '',
      maitre_ouvrage_ville: mo.ville || '', maitre_ouvrage_pays: mo.pays || 'France',
      maitre_ouvrage_telephone: mo.telephone || '', maitre_ouvrage_email: mo.email || '',
      maitre_ouvrage_pieces_identite: Array.isArray(mo.pieces_identite) ? mo.pieces_identite : [],
    }));
  }, [propositionSnapshot, calculData]);

  // === Custom Hooks ===
  const { siretValidation } = useSiretValidation(formData, setFormData);
  const docs = useDocuments(souscriptionCreee, etape);
  const sig = useSignature(souscriptionCreee, formData, user, setEtape);

  const persistence = useWorkflowPersistence({
    user, resumeId,
    souscriptionCreee, setSouscriptionCreee,
    etape, setEtape,
    formData, setFormData,
    setDocumentsGeneres: docs.setDocumentsGeneres,
    setDocumentPdfUrl: docs.setDocumentPdfUrl,
    initDocsForSouscription: docs.initDocsForSouscription,
    documentsLus: docs.documentsLus, setDocumentsLus: docs.setDocumentsLus,
    documentScrolled: docs.documentScrolled, setDocumentScrolled: docs.setDocumentScrolled,
    codeSMSEnvoye: sig.codeSMSEnvoye, setCodeSMSEnvoye: sig.setCodeSMSEnvoye,
    signatureValidee: sig.signatureValidee, setSignatureValidee: sig.setSignatureValidee,
    telephoneSignature: sig.telephoneSignature, setTelephoneSignature: sig.setTelephoneSignature,
    setFtpeAdhesionId: sig.setFtpeAdhesionId, setFtpeAccepted: sig.setFtpeAccepted,
    setFtpeOtpEnvoye: sig.setFtpeOtpEnvoye, setFtpeStatus: sig.setFtpeStatus,
    setFtpeTelephone: sig.setFtpeTelephone,
    // V36.96 — Lien explicite pour persister `ftpe_adhesion_id` sur la souscription
    ftpeAdhesionId: sig.ftpeAdhesionId,
  });

  // Souscripteur déjà adhérent (badge proposition ou adhésion FTPE en cours) : bloc FTPE masqué (Documents + Paiement)
  useEffect(() => {
    const id = souscriptionCreee?.id;
    if (!id) return;
    axios.get(`${API}/souscriptions/${id}/ftpe-deja-adherent`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (r.data?.deja_adherent !== true) return;
        sig.setIsFtpeMember(true); sig.setFtpeAccepted(true); sig.setFtpeStatus('paid');
      })
      .catch(() => {});
  }, [souscriptionCreee?.id]);


  // Detect souscription type
  const typeSouscription = calculData?.type_souscription === 'avenant' ? 'avenant' : (propositionSnapshot?.type_souscription || calculData?.type_souscription);
  const isAvenant = typeSouscription === 'avenant';
  const isNominative = typeSouscription === 'nominative';

  // Tarification
  const tarificationData = persistence.isResuming ? souscriptionCreee?.tarification : calculData?.tarification;
  const montantTotalHT =
    // PRIORITÉ 1 : Snapshot Proposition ESS (source de vérité backend)
    (propositionSnapshot?.montant_ht && propositionSnapshot.montant_ht > 0
      ? parseFloat(propositionSnapshot.montant_ht) : 0) ||
    calculData?.montants_personnalises?.montant_ht_personnalise ||
    calculData?.tarification?.montants_personnalises?.montant_ht_personnalise ||
    tarificationData?.montant_total_ht || tarificationData?.montant_ht ||
    souscriptionCreee?.montant_ht_contribution ||
    ((tarificationData?.montant_ht_gros_oeuvre || 0) + (tarificationData?.montant_ht_second_oeuvre || 0) +
     (tarificationData?.montant_ht_travaux_publics || 0) + (tarificationData?.montant_ht_pib || 0)) || 0;

  const caHT = persistence.isResuming
    ? (souscriptionCreee?.chiffre_affaires || 0)
    : (propositionSnapshot?.ca_ht || calculData?.ca_ht || 0);

  const montantTTC = (() => {
    // PRIORITÉ 1 : Snapshot Proposition ESS (source de vérité backend)
    // — évite toute divergence entre le « Montant Global » du PDF devis
    // et le montant affiché à l'étape Paiement du workflow.
    if (propositionSnapshot?.montant_ttc && propositionSnapshot.montant_ttc > 0) {
      return parseFloat(propositionSnapshot.montant_ttc);
    }
    // PRIORITÉ 1bis : montant_global du snapshot (label PDF "Montant Global")
    if (propositionSnapshot?.montant_global && propositionSnapshot.montant_global > 0) {
      return parseFloat(propositionSnapshot.montant_global);
    }
    const ttcPerso = calculData?.montants_personnalises?.montant_ttc_personnalise
      || calculData?.tarification?.montants_personnalises?.montant_ttc_personnalise;
    if (ttcPerso && parseFloat(ttcPerso) > 0) return parseFloat(ttcPerso);
    // V35.66 — Lecture exhaustive du TTC depuis la tarification backend AVANT
    // tout fallback calculé localement. La valeur backend correspond au
    // "Montant Global - Contribution Solidaire Proposée" du Devis ESS PDF.
    const ttcBackend = (
      calculData?.tarification?.montant_ttc
      || calculData?.tarification?.montant_global
      || calculData?.tarification?.montant_total_ttc
      || souscriptionCreee?.tarification?.montant_ttc
      || souscriptionCreee?.tarification?.montant_global
      || souscriptionCreee?.montant_global
      || souscriptionCreee?.montant_ttc
    );
    if (ttcBackend && parseFloat(ttcBackend) > 0) return parseFloat(ttcBackend);
    // PRIORITÉ 2 : Détail des taxes (contributions solidaires explicites)
    const taxesDetail = calculData?.taxes_personnalisees || calculData?.tarification?.taxes?.detail
      || calculData?.tarification?.taxes_personnalisees || souscriptionCreee?.taxes_personnalisees
      || souscriptionCreee?.tarification?.taxes?.detail;
    if (taxesDetail && Array.isArray(taxesDetail) && taxesDetail.length > 0 && montantTotalHT > 0) {
      const totalContributions = taxesDetail.reduce((sum, t) => {
        const montant = parseFloat(t.montant || 0);
        return montant > 0 ? sum + montant : sum + (montantTotalHT * parseFloat(t.taux || 0) / 100);
      }, 0);
      return Math.round((montantTotalHT + totalContributions) * 100) / 100;
    }
    // V35.66 — Le fallback ×1.38 historique produisait des montants
    // DIVERGENTS du PDF Devis ESS (3 021,44 × 1.38 = 4 169,59 ≠ 4 332,07
    // affiché sur le devis). On retourne 0 plutôt qu'un montant incorrect ;
    // l'UI affichera "Calcul en cours…" et bloque le passage à l'étape
    // paiement tant qu'on n'a pas une valeur officielle.
    return 0;
  })();

  // Subscription creation hook
  // V36.97 — Fusionner `propositionSnapshot` dans `calculData` pour s'assurer
  // que les `activites_selectionnees` / `activites_details` chargées depuis le
  // backend snapshot soient bien transmises à la création de souscription,
  // même quand `location.state` est null (lien direct `?proposition=...`).
  const calculDataEffective = useMemo(() => {
    if (!propositionSnapshot) return calculData;
    const snapNominatif = propositionSnapshot.type_souscription && propositionSnapshot.type_souscription !== 'annuel';
    return {
      ...calculData,
      type_souscription: calculData?.type_souscription === 'avenant' ? 'avenant' : (propositionSnapshot.type_souscription || calculData?.type_souscription),
      ...(snapNominatif && {
        nombre_jours: calculData?.nombre_jours || propositionSnapshot.nombre_jours,
        operation_nominative: calculData?.operation_nominative || propositionSnapshot.operation_nominative,
      }),
      // calculData prend le pas pour les champs déjà initialisés côté simulateur,
      // mais les activités du snapshot remplacent les valeurs vides/absentes.
      activites_selectionnees:
        (calculData?.activites_selectionnees && Object.keys(calculData.activites_selectionnees || {}).length > 0
          ? calculData.activites_selectionnees
          : propositionSnapshot.activites_selectionnees) || {},
      activites_details:
        (calculData?.activites_details && Object.keys(calculData.activites_details || {}).length > 0
          ? calculData.activites_details
          : propositionSnapshot.activites_details) || {},
    };
  }, [calculData, propositionSnapshot]);

  const { loading, fieldErrors, validateField, creerSouscription } = useSubscriptionCreation({
    formData, selectedClient, calculData: calculDataEffective, montantTotalHT, montantTTC,
    souscriptionCreee, setSouscriptionCreee, setEtape,
    persistence, sig, docs, isNominative,
  });

  // Member selection hook
  const { handleSelectMembre, formatDateForInput, formatCodeNaf } = useMemberSelection({
    membresAdherents, setSelectedClient, setFormData, sig,
  });

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    validateField(fieldName, value);
  };

  // Sync phone country
  useEffect(() => {
    if (formData.indicatif_telephone) {
      const paysMatch = PAYS_OPTIONS.find(p => p.indicatif === formData.indicatif_telephone);
      if (paysMatch && paysMatch.code !== selectedPaysTelephone?.code) setSelectedPaysTelephone(paysMatch);
    }
  }, [formData.indicatif_telephone, selectedPaysTelephone?.code]);

  // Fetch membres adherents
  const fetchMembresAdherents = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/coopers/membres-adherents`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.members) ? response.data.members : []));
    } catch { setClients([]); }
  };

  // Init effect - run once on mount
  const initDoneRef = useRef(false);
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    if (!user || !['cooper', 'super_admin', 'admin', 'member'].includes(user.role)) {
      toast.error('Acces non autorise');
      navigate('/');
      return;
    }
    if (resumeId) {
      persistence.loadSouscriptionForResume(resumeId);
      fetchMembresAdherents();
      return;
    }
    if (calculData?.type_souscription === 'avenant' && calculData?.membre_info) {
      const membreInfo = calculData.membre_info;
      const contratParentData = calculData.contrat_parent || {};
      setFormData(prev => ({
        ...prev,
        raison_sociale: membreInfo.raison_sociale || contratParentData.raison_sociale || '',
        siret: membreInfo.siret || contratParentData.siret || '',
        adresse_entreprise: membreInfo.adresse || contratParentData.adresse_entreprise || '',
        code_postal: membreInfo.code_postal || contratParentData.code_postal || '',
        ville: membreInfo.ville || contratParentData.ville || '',
        telephone: membreInfo.telephone || contratParentData.telephone || '',
        email: membreInfo.email || contratParentData.email || '',
        nom_gerant: membreInfo.nom_gerant || contratParentData.nom_gerant || '',
        prenom_gerant: membreInfo.prenom_gerant || contratParentData.prenom_gerant || '',
        chiffre_affaires: calculData.ca_ht || contratParentData.chiffre_affaires || 0,
        forme_juridique: membreInfo.forme_juridique || contratParentData.forme_juridique || contratParentData.statut_juridique || '',
        date_creation_entreprise: formatDateForInput(membreInfo.date_creation_entreprise || contratParentData.date_creation_entreprise || ''),
        pays: membreInfo.pays || contratParentData.pays || contratParentData.pays_code || 'FR',
        pays_code: membreInfo.pays_code || contratParentData.pays_code || contratParentData.pays || 'FR',
        telephone_indicatif: membreInfo.telephone_indicatif || contratParentData.telephone_indicatif || '+590',
        code_naf: formatCodeNaf(membreInfo.code_naf || contratParentData.code_naf || ''),
        effectif: membreInfo.effectif || contratParentData.effectif || '',
        fonction_gerant: membreInfo.fonction_gerant || contratParentData.fonction_gerant || '',
        territoire: membreInfo.territoire || contratParentData.territoire || '',
        territoire_id: membreInfo.territoire_id || contratParentData.territoire_id || '',
        // V36 — Multi-territoires (depuis le contrat parent ou le membre)
        territoires: (Array.isArray(membreInfo.territoires) && membreInfo.territoires.length
          ? membreInfo.territoires
          : (Array.isArray(contratParentData.territoires) && contratParentData.territoires.length
              ? contratParentData.territoires
              : [])),
        territoires_ca: (membreInfo.territoires_ca && typeof membreInfo.territoires_ca === 'object')
          ? membreInfo.territoires_ca
          : (contratParentData.territoires_ca && typeof contratParentData.territoires_ca === 'object'
              ? contratParentData.territoires_ca
              : {}),
      }));
      setSelectedClient('existant');
      toast.info(`Avenant pour ${membreInfo.raison_sociale || contratParentData.raison_sociale} - Completez les informations si necessaire`);
    }
    fetchMembresAdherents();
  }, []);

  // Auto-selection du membre si donnees preremplies depuis le simulateur
  // (location.state) OU depuis le snapshot Proposition ESS (chargé via API
  // quand on arrive avec `?proposition=...` depuis le tableau des membres
  // ou un email Brevo).
  useEffect(() => {
    if (prefillAppliedRef.current || membresAdherents.length === 0) return;
    const prefill = calculData?.membre_prefill || propositionSnapshot?.membre_prefill || null;
    if (!prefill) return;
    let match = null;
    if (prefill.membre_id) match = membresAdherents.find(m => m.id === prefill.membre_id);
    if (!match && prefill.raison_sociale) match = membresAdherents.find(m => m.raison_sociale?.toLowerCase().trim() === prefill.raison_sociale?.toLowerCase().trim());
    if (!match && prefill.siret) { const s = prefill.siret.replace(/\s/g, ''); match = membresAdherents.find(m => (m.siret || '').replace(/\s/g, '') === s); }
    if (match) {
      prefillAppliedRef.current = true;
      handleSelectMembre(match.id);
      // Avancer directement à l'étape Entreprise (étape 2) pour matérialiser
      // le pré-remplissage sans clic supplémentaire. L'utilisateur peut
      // toujours revenir à l'étape 1 si besoin via la flèche retour.
      if (montantTTC > 0) setEtape((curr) => (curr === 1 ? 2 : curr));
    }
  }, [membresAdherents, propositionSnapshot]);

  // Steps config
  const steps = isNominative ? [
    { id: 'membre', label: isAvenant ? 'Verification' : 'Membre', icon: User },
    { id: 'entreprise', label: 'Entreprise', icon: Building2 },
    { id: 'maitre_ouvrage', label: "Maitre d'ouvrage", icon: HardHat },
    { id: 'documents', label: isAvenant ? 'Avenant' : 'Documents', icon: FileText },
    { id: 'signature', label: 'Signature', icon: FileSignature },
    { id: 'paiement', label: 'Paiement', icon: Banknote },
    { id: 'confirmation', label: 'Termine', icon: CheckCircle2 }
  ] : [
    { id: 'membre', label: isAvenant ? 'Verification' : 'Membre', icon: User },
    { id: 'entreprise', label: 'Entreprise', icon: Building2 },
    { id: 'documents', label: isAvenant ? 'Avenant' : 'Documents', icon: FileText },
    { id: 'signature', label: 'Signature', icon: FileSignature },
    { id: 'paiement', label: 'Paiement', icon: Banknote },
    { id: 'confirmation', label: 'Termine', icon: CheckCircle2 }
  ];

  const getStepIndex = (etapeNum) => {
    if (isNominative) return etapeNum;
    return etapeNum <= 2 ? etapeNum : etapeNum - 1;
  };

  if (persistence.loadingResume) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-cyan-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Chargement de la souscription</h2>
          <p className="text-slate-400">Reprise en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-slate-900">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L2c+PC9zdmc+')] opacity-40" />
      <div className="relative max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2" data-testid="workflow-header-nav">
            {etape > 1 ? (
              <Button
                variant="ghost"
                onClick={() => setEtape(etape - 1)}
                className="text-white bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 hover:border-white/50 transition-all"
                data-testid="step-prev-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Étape {etape - 1}
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => {
                  // V36.85 — Retour role-aware : un super_admin ne doit pas
                  // être renvoyé sur /cooper/dashboard via navigate(-1) si
                  // l'historique provient de la route préfixée /cooper.
                  const historyIdx = window.history.state?.idx;
                  if (typeof historyIdx === 'number' && historyIdx > 0) {
                    navigate(-1);
                  } else {
                    navigate(getDashboardRouteForRole(user?.role));
                  }
                }}
                className="text-white bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 hover:border-white/50 transition-all"
                data-testid="workflow-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate(getDashboardRouteForRole(user?.role))}
              className="text-[#0a3d62] bg-[#DEA900] hover:bg-[#f5cc3e] border border-[#DEA900] transition-all"
              data-testid="workflow-home-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Accueil
            </Button>
          </div>
          <div className="flex-1 text-center min-w-0">
            {persistence.isResuming && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-4 mr-2">
                <RefreshCw className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-300">Reprise en cours</span>
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 mb-4">
              <Sparkles className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-300">Workflow Intelligent</span>
            </div>
            <h1 className="text-4xl font-black bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Souscription RCD Solidaire
            </h1>
            <p className="text-slate-400 mt-2">
              {persistence.isResuming ? `Reprise de la souscription ${souscriptionCreee?.numero_police || ''}` : 'Processus securise et automatise'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
          <StepIndicator steps={steps} currentStep={getStepIndex(etape)} />
        </div>

        {/* Steps */}
        {etape === 1 && (
          <StepMemberSelection ctx={{
            caHT, montantTotalHT, montantTTC, membresAdherents,
            selectedClient, handleSelectMembre,
            setEtape: (n) => {
              if (n > 1 && !(montantTTC > 0)) {
                toast.error('Montant non calculé', {
                  description: "Aucun montant n'a été calculé pour cette proposition. Relancez le simulateur BATICO (CA et activités) avant de souscrire.",
                  duration: 7000,
                });
                return;
              }
              setEtape(n);
            },
            formData, isNominative,
            typeSouscription: typeSouscription
          }} />
        )}

        {etape === 2 && (
          <StepEntrepriseForm ctx={{
            formData, setFormData, handleFieldChange, fieldErrors, siretValidation,
            selectedPays, setSelectedPays, selectedPaysTelephone, setSelectedPaysTelephone,
            isNominative, loading, creerSouscription, setEtape
          }} />
        )}

        {etape === 3 && isNominative && (
          <StepMaitreOuvrage ctx={{ formData, setFormData, loading, creerSouscription, setEtape }} />
        )}

        {etape === 4 && (
          <StepRecapDocuments ctx={{
            loading, documentsGeneres: docs.documentsGeneres, documentPdfUrl: docs.documentPdfUrl,
            souscriptionCreee, generationProgress: docs.generationProgress,
            documentScrolled: docs.documentScrolled, setDocumentScrolled: docs.setDocumentScrolled,
            showPdfViewer: docs.showPdfViewer, setShowPdfViewer: docs.setShowPdfViewer,
            pdfViewerUrl: docs.pdfViewerUrl, setPdfViewerUrl: docs.setPdfViewerUrl,
            pdfViewerTitle: docs.pdfViewerTitle, setPdfViewerTitle: docs.setPdfViewerTitle,
            pdfViewerLoading: docs.pdfViewerLoading, setPdfViewerLoading: docs.setPdfViewerLoading,
            downloadingDoc: docs.downloadingDoc, setDownloadingDoc: docs.setDownloadingDoc,
            ftpeAccepted: sig.ftpeAccepted, ftpeLoading: sig.ftpeLoading,
            handleCreateFtpeAdhesion: sig.handleCreateFtpeAdhesion,
            isFtpeMember: sig.isFtpeMember,
            handleSaveForLater: persistence.handleSaveForLater,
            savingForLater: persistence.savingForLater, setEtape, isNominative,
            genererDocumentsAvecId: docs.genererDocumentsAvecId,
            ftpeAdhesionId: sig.ftpeAdhesionId,
            formData,
          }} />
        )}

        {etape === 5 && (
          <StepSignatureSMS ctx={{
            ftpeSmsLoading: sig.ftpeSmsLoading, rcdsSmsLoading: sig.rcdsSmsLoading,
            ftpeSigned: sig.ftpeSigned, ftpeOtpEnvoye: sig.ftpeOtpEnvoye,
            ftpeTelephone: sig.ftpeTelephone, setFtpeTelephone: sig.setFtpeTelephone,
            ftpeCodeOtp: sig.ftpeCodeOtp, setFtpeCodeOtp: sig.setFtpeCodeOtp,
            ftpeAdhesionId: sig.ftpeAdhesionId, isFtpeMember: sig.isFtpeMember,
            handleFtpeSendOtp: sig.handleFtpeSendOtp,
            handleFtpeValidateSignature: sig.handleFtpeValidateSignature,
            renvoyerCodeFTPE: sig.renvoyerCodeFTPE,
            signatureValidee: sig.signatureValidee,
            telephoneSignature: sig.telephoneSignature, setTelephoneSignature: sig.setTelephoneSignature,
            codeSMSEnvoye: sig.codeSMSEnvoye, codeSMS: sig.codeSMS, setCodeSMS: sig.setCodeSMS,
            demoCode: sig.demoCode,
            envoyerCodeSMS: sig.envoyerCodeSMS, validerCodeSMS: sig.validerCodeSMS,
            renvoyerCodeSMS: sig.renvoyerCodeSMS,
            souscriptionId: souscriptionCreee?.id
          }} />
        )}

        {etape === 6 && (
          <StepPaiement ctx={{
            loading, montantTTC, souscriptionCreee, setEtape,
            ftpePaid: sig.ftpePaid, ftpeReferenceVirement: sig.ftpeReferenceVirement,
            handleFtpePaymentVirement: sig.handleFtpePaymentVirement, isFtpeMember: sig.isFtpeMember,
            // V37.03 — Le ftpeLoading manquait dans le ctx étape 6, conduisant à
            // un `undefined` ambigu dans le bouton FTPE. On le passe explicitement.
            ftpeLoading: sig.ftpeLoading,
            formData,
            paiementEchelonne, setPaiementEchelonne,
            // V36.74 — Échéancier personnalisé Super Admin issu de la
            // proposition (pré-remplit SepaMandateBlock / GoCardless).
            echelonnementOverride: propositionSnapshot?.echelonnement_override || souscriptionCreee?.echelonnement_override || null,
          }} />
        )}

        {etape === 7 && (
          <StepConfirmation ctx={{
            formData, montantTTC, souscriptionCreee, paiementEchelonne,
            // V36.74 — propagation à StepConfirmation pour le bloc SEPA persistant.
            echelonnementOverride: propositionSnapshot?.echelonnement_override || souscriptionCreee?.echelonnement_override || null,
          }} />
        )}
      </div>
    </div>
  );
}
